/**
 * Calculate voltage
 * Formula: V = (H x 1000 x S) / (60 x A)
 *
 * @param {number} heatInput - Heat input in kj/mm or kj/in
 * @param {number} travelSpeed - Travel speed in mm/min or in/min
 * @param {number} amperage - Amperage in amperes
 * @returns {number} Voltage in volts (rounded to 2 decimals)
 */
export function calculateVoltage(heatInput, travelSpeed, amperage) {
  if (!isValidNumber(heatInput) || !isValidNumber(travelSpeed) || !isValidNumber(amperage)) {
    return null;
  }

  if (amperage === 0) return null;

  const voltage = (heatInput * travelSpeed * 1000) / (amperage * 60);
  return roundToDecimals(voltage, 2);
}

/**
 * Calculate amperage
 * Formula: A = (H x 1000 x S) / (60 x V)
 *
 * @param {number} heatInput - Heat input in kj/mm or kj/in
 * @param {number} travelSpeed - Travel speed in mm/min or in/min
 * @param {number} voltage - Voltage in volts
 * @returns {number} Amperage in amperes (rounded to 2 decimals)
 */
export function calculateAmperage(heatInput, travelSpeed, voltage) {
  if (!isValidNumber(heatInput) || !isValidNumber(travelSpeed) || !isValidNumber(voltage)) {
    return null;
  }

  if (voltage === 0) return null;

  const amperage = (heatInput * travelSpeed * 1000) / (voltage * 60);
  return roundToDecimals(amperage, 2);
}

/**
 * Calculate travel speed
 * Formula: S = (60 x V x A) / (1000 x H)
 *
 * @param {number} voltage - Voltage in volts
 * @param {number} amperage - Amperage in amperes
 * @param {number} heatInput - Heat input in kj/mm or kj/in
 * @returns {number} Travel speed in mm/min or in/min (rounded to 2 decimals)
 */
export function calculateTravelSpeed(voltage, amperage, heatInput) {
  if (!isValidNumber(voltage) || !isValidNumber(amperage) || !isValidNumber(heatInput)) {
    return null;
  }

  if (heatInput === 0) return null;

  const travelSpeed = (voltage * amperage * 60) / (heatInput * 1000);
  return roundToDecimals(travelSpeed, 2);
}

/**
 * Calculate heat input
 * Formula: H = (60 x V x A) / (1000 x S)
 *
 * @param {number} voltage - Voltage in volts
 * @param {number} amperage - Amperage in amperes
 * @param {number} travelSpeed - Travel speed in mm/min or in/min
 * @returns {number} Heat input in kj/mm or kj/in (rounded to 2 decimals)
 */
export function calculateHeatInput(voltage, amperage, travelSpeed) {
  if (!isValidNumber(voltage) || !isValidNumber(amperage) || !isValidNumber(travelSpeed)) {
    return null;
  }

  if (travelSpeed === 0) return null;

  const heatInput = (voltage * amperage * 60) / (travelSpeed * 1000);
  return roundToDecimals(heatInput, 2);
}

/**
 * Validate if a value is a valid number
 * @param {any} value - Value to validate
 * @returns {boolean} True if value is a valid number
 */
function isValidNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && !Number.isNaN(value);
}

/**
 * Round a number to specified decimal places
 * @param {number} value - Value to round
 * @param {number} decimals - Number of decimal places
 * @returns {number} Rounded value
 */
function roundToDecimals(value, decimals) {
  const multiplier = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}
